import { ReactElement, Suspense } from 'react';
import { RouteObject } from 'react-router-dom';
import { SplashScreenLayout } from '../../components/layouts/splash-screen.layout';
import DashboardLayout from 'app/layouts/dashboard-layout';
import {
    GatewayListPage,
    GatewayDashboardPage,
    GatewaySettingsPage,
    TransactionsListPage,
    TransactionShowPage,
    TransactionAnalyticsPage,
    CustomerBehaviorAnalyticsPage,
    RangeReportPage,
    BankAccountsPage,
    WithdrawListPage,
    WithdrawShowPage,
    ProfileSettingsPage,
    TicketPage,
    MarketingPage,
    MarketingWithdrawShowPage,
    NewsListDataTable,
    ShowNews,
    Partners,
    Report,
    PartnerFallback,
    TicketFallback,
    MarketingFallback,
    ProfileFallback,
    DataTableFallback,
    WithdrawFallback,
    WithdrawDetailsFallback,
    TransactionDetailsFallback,
    TransactionAnalyticsFallback,
    GatewayDashboardFallback,
    GatewaySettingsFallback,
} from './pages.registry';
import { ProtectedRoute } from './protected-route';

const lazyRoute = (page: ReactElement, fallback: ReactElement) => (
    <Suspense fallback={fallback}>{page}</Suspense>
);

export const panelRoutes = (isAccepted: boolean): RouteObject => ({
    element: isAccepted ? <DashboardLayout /> : <SplashScreenLayout />,
    children: [
        {
            path: '/',
            element: <GatewayListPage />,
        },
        {
            path: '/:w',
            element: lazyRoute(
                <GatewayDashboardPage />,
                <GatewayDashboardFallback />,
            ),
        },
        {
            path: '/:w/settings/*',
            element: lazyRoute(
                <GatewaySettingsPage />,
                <GatewaySettingsFallback />,
            ),
        },
        {
            path: '/:w/transaction',
            element: lazyRoute(
                <ProtectedRoute permissionKey="gateway-transaction-show">
                    <TransactionsListPage />
                </ProtectedRoute>,
                <DataTableFallback
                    title="تراکنش ها"
                    buttonLabels={['جستجو']}
                />,
            ),
        },
        {
            path: '/:w/transaction/:id',
            element: lazyRoute(
                <ProtectedRoute permissionKey="gateway-transaction-detail">
                    <TransactionShowPage />
                </ProtectedRoute>,
                <TransactionDetailsFallback />,
            ),
        },
        {
            path: '/:w/transaction-analytics',
            element: lazyRoute(
                <ProtectedRoute permissionKey="gateway-transaction-analytics-show">
                    <TransactionAnalyticsPage />
                </ProtectedRoute>,
                <TransactionAnalyticsFallback />,
            ),
        },
        {
            path: '/:w/customer-behavior-analytics',
            element: lazyRoute(
                <ProtectedRoute permissionKey="gateway-customer-behavior-analytics-show">
                    <CustomerBehaviorAnalyticsPage />
                </ProtectedRoute>,
                <TransactionAnalyticsFallback title="تحلیل رفتار مشتری" />,
            ),
        },
        {
            path: '/:w/range-report',
            element: lazyRoute(
                <ProtectedRoute permissionKey="gateway-range-report-export">
                    <RangeReportPage />
                </ProtectedRoute>,
                <TransactionAnalyticsFallback title="دریافت گزارش بازه زمانی" />,
            ),
        },
        {
            path: '/:w/withdraw',
            element: lazyRoute(
                <ProtectedRoute permissionKey="gateway-withdraw-show">
                    <WithdrawListPage />
                </ProtectedRoute>,
                <WithdrawFallback />,
            ),
        },
        {
            path: '/:w/withdraw/:id',
            element: lazyRoute(
                <ProtectedRoute permissionKey="gateway-withdraw-detail">
                    <WithdrawShowPage />
                </ProtectedRoute>,
                <WithdrawDetailsFallback />,
            ),
        },
        {
            path: 'profile/*',
            element: lazyRoute(<ProfileSettingsPage />, <ProfileFallback />),
        },
        {
            path: 'bank',
            element: lazyRoute(
                <BankAccountsPage />,
                <DataTableFallback
                    title="حساب های بانکی"
                    buttonLabels={['جستجو', 'افزودن حساب بانکی']}
                />,
            ),
        },
        {
            path: 'ticket',
            element: lazyRoute(<TicketPage />, <TicketFallback />),
            children: [
                {
                    path: ':ticketId',
                    element: <TicketPage />,
                },
            ],
        },
        {
            path: 'marketing/withdraws/:id',
            element: lazyRoute(
                <MarketingWithdrawShowPage />,
                <WithdrawDetailsFallback />,
            ),
        },
        {
            path: 'marketing/*',
            element: lazyRoute(<MarketingPage />, <MarketingFallback />),
        },
        {
            path: 'report',
            element: lazyRoute(
                <Report />,
                <DataTableFallback
                    title="گزارش های فراخوانی"
                    buttonLabels={['جستجو']}
                />,
            ),
        },
        {
            path: 'partner',
            element: lazyRoute(<Partners />, <PartnerFallback />),
        },
        {
            path: 'news',
            element: lazyRoute(
                <NewsListDataTable />,
                <DataTableFallback title="لیست اطلاعیه ها" />,
            ),
            children: [
                {
                    path: ':id',
                    element: <ShowNews />,
                },
            ],
        },
    ],
});
